import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Grid, iconButtonGhostMiniClasses, tabClasses, Tabs } from '@kc-console/common-ui';

import DetailMultiViewContent from './DetailMultiViewContent';
import DetailMultiViewEmptyState, { SUMMARY_LIMIT } from './DetailMultiViewEmptyState';

import type { ReactNode } from 'react';
import type { MultiSectionItem, MultiSectionItemWithValue } from './types';

interface DetailMultiViewProps {
  sections: MultiSectionItem[];
  /**
   * 모든 섹션이 숨겨진 경우 표시할 폴백 콘텐츠
   * - 미지정 시 기본 EmptyState가 표시됩니다.
   */
  fallback?: ReactNode;
  /**
   * 선택된 항목 개수
   * - 상태 전환 감지에 사용 (1개, 2개, 2개 초과)
   * - 상태 전환 시 가장 좌측(첫 번째) 탭으로 초기화
   */
  selectedCount?: number;
}

type SelectionState = 'single' | 'double' | 'multiple';

const getSelectionState = (count: number): SelectionState => {
  if (count <= 1) return 'single';
  if (count === 2) return 'double';
  return 'multiple';
};

const DetailMultiView = ({ sections, selectedCount = 0, fallback }: DetailMultiViewProps) => {
  const sectionsWithValue = transformDetailSection(sections, selectedCount);

  // 가장 좌측(첫 번째) 탭 값 계산
  const getFirstTabValue = useCallback(() => sectionsWithValue[0]?.value, [sectionsWithValue]);

  const [selectedTab, setSelectedTab] = useState(getFirstTabValue);

  // 선택 상태 추적
  const currentState = getSelectionState(selectedCount);
  const prevStateRef = useRef<SelectionState>(currentState);

  const tabItems = sectionsWithValue?.map(({ label, value }) => ({ label, value }));

  useLayoutEffect(() => {
    const prevState = prevStateRef.current;
    const isStateChanged = prevState !== currentState;

    // 상태 전환 시 가장 좌측(첫 번째) 탭으로 초기화
    if (isStateChanged) {
      setSelectedTab(getFirstTabValue());
      prevStateRef.current = currentState;
      return;
    }

    // 현재 탭이 유효하지 않은 경우에만 초기화 (기존 로직 유지)
    const isSelectedTabValid = sectionsWithValue.some(section => section.value === selectedTab);
    if (!isSelectedTabValid) {
      setSelectedTab(getFirstTabValue());
    }
  }, [sections, selectedTab, selectedCount, currentState, sectionsWithValue, getFirstTabValue]);

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
  };

  // 표시할 컨텐츠가 없는 경우
  if (sections.length > 0 && sectionsWithValue.length === 0) {
    return <FallbackContainer>{fallback ?? <DefaultFallback />}</FallbackContainer>;
  }

  return (
    <Grid display="flex" flexDirection="column" gap="16px" minHeight="100%">
      <StickyTabs>
        <StyledTabs items={tabItems} value={selectedTab} onChange={handleTabChange} />
      </StickyTabs>
      <DetailMultiViewContent sections={sectionsWithValue} selectedTab={selectedTab} />
    </Grid>
  );
};

export default DetailMultiView;

const DefaultFallback = () => <DetailMultiViewEmptyState type="summary_unsupported" />;

const FallbackContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  flex: 1;
`;

const transformDetailSection = (
  sections: MultiSectionItem[],
  selectedCount: number,
): MultiSectionItemWithValue[] => {
  return sections
    .filter(section => !section.isHidden)
    .map(section => ({
      label: section.header,
      value: `detail-content-${section.header}`,
      content: section.content,
      emptyState: resolveSectionEmptyState(section, selectedCount),
    }));
};

const resolveSectionEmptyState = (section: MultiSectionItem, selectedCount: number) => {
  if (section.emptyState) {
    return section.emptyState;
  }

  if (section.emptyStatePreset === 'summary_limit' && selectedCount > SUMMARY_LIMIT) {
    return <DetailMultiViewEmptyState type="summary_limit" />;
  }

  return undefined;
};

const StickyTabs = styled('div')`
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.color['background/default']};
  z-index: ${({ theme }) => theme.zIndex.sticky};
`;

const StyledTabs = styled(Tabs)(
  ({ theme }) => css`
    flex: 1;
    /**
     * 포커스 링의 가로 방향이 탭 요소 밖으로 나가므로, scroller(overflow-x: auto)의
     * 왼쪽 경계에 닿는 첫 번째 탭에서 링이 잘린다. 탭 목록에 왼쪽 여유를 주고 같은
     * 크기만큼 당겨, 레이아웃 위치는 그대로 두고 클리핑 여유만 확보한다.
     */
    margin-left: -4px;
    .MuiTabs-flexContainer {
      padding-left: 4px;
    }
    &::after {
      z-index: -1;
    }
    .${tabClasses.item} {
      padding-left: 0;
      padding-right: 0;
    }
    .${tabClasses.item}:not(last-of-type) {
      margin-right: 24px;
    }
    .${tabClasses.item}:not(.Mui-selected, .Mui-disabled:has(.${iconButtonGhostMiniClasses.root}:hover)):hover {
      background-color: initial;
    }
    /**
     * 공통 Tab은 selected 좌우 세로 보더와 hover 하단선을 ::before 로 그리지만,
     * 앵커 탭은 밑줄형이라 두 장식을 감춘다. (KIC-36462)
     * 다만 공통 Tab은 포커스 링도 같은 ::before 에 그리므로(focusRing('inside') 기본값),
     * :focus-visible 만 제외해 키보드 포커스 표시가 남도록 한다. (KIC-63347)
     */
    .${tabClasses.item}:not(:focus-visible)::before {
      display: none;
    }
    /**
     * MUI Tab root 는 overflow: hidden 이라 요소 밖으로 나간 ::before 가 잘린다.
     * 가로 방향 포커스 링이 보이도록 포커스 상태에서만 클리핑을 푼다. 공통 Tab 이
     * max-width: initial 로 MUI 의 maxWidth 제한을 이미 해제했으므로 라벨이 잘릴 일은 없다.
     */
    .${tabClasses.item}:focus-visible {
      overflow: visible;
    }
    /**
     * :focus-visible 에서 ::before 가 살아나면 공통 Tab 의 selected(top: 0 · height: 100%)와
     * hover(inset 0 · height: 1px · border-bottom) 선언이 포커스 링과 같은 레이어에 남아
     * 링 형태가 깨진다. hover 규칙보다 우선순위가 높도록 :not(.Mui-disabled) 를 더해
     * focusRing('inside') 이 그리는 사각형만 남긴다.
     * 가로까지 inside 로 두면 앵커 탭은 좌우 padding 이 0 이라 안쪽 링이 라벨 텍스트를
     * 덮으므로, 세로만 inside(2px) 로 유지하고 가로는 바깥(-2px)으로 내보낸다.
     */
    .${tabClasses.item}:focus-visible:not(.Mui-disabled)::before {
      inset: 2px -2px; /* 세로: focusRing('inside') 기본 offset · 가로: outside */
      height: auto;
      border: 2px solid ${theme.color['focusRing/inverse']}; /* focusRing 안쪽 링 색 */
    }
    .${tabClasses.item}.Mui-selected::after {
      top: initial;
      bottom: 0;
    }
    .${tabClasses.item}:not(.Mui-selected, .Mui-disabled:has(.${iconButtonGhostMiniClasses.root}:hover)):hover::after {
      top: initial;
      bottom: 0;
      border-bottom-color: ${theme.color['border/strong/default']};
    }
  `,
);
